import React from 'react';
import { motion } from 'framer-motion';
import { LoadingSpinner } from './LoadingStates';

interface PageLoaderProps {
  message?: string;
  showProgress?: boolean;
  progress?: number;
  className?: string;
}

export const PageLoader: React.FC<PageLoaderProps> = ({
  message = 'Cargando...',
  showProgress = false,
  progress = 0,
  className = ''
}) => {
  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br from-deep-space/60 via-cosmic-violet/55 to-black/70 ${className}`}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        {/* Spinner principal */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="mb-8"
        >
          <LoadingSpinner size="xl" color="primary" />
        </motion.div>

        {/* Mensaje de carga */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-2xl font-orbitron font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyber-blue to-neon-purple mb-4"
        >
          {message}
        </motion.h2>

        {/* Barra de progreso opcional */}
        {showProgress && (
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: '100%' }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="w-64 mx-auto"
          >
            <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
              <motion.div
                className="bg-gradient-to-r from-cyber-blue to-neon-purple h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            </div>
            <p className="text-sm text-gray-400">{Math.round(progress)}%</p>
          </motion.div>
        )}

        {/* Efectos de fondo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 6 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-cyber-blue rounded-full opacity-20"
              style={{
                left: `${20 + i * 15}%`,
                top: `${30 + (i % 2) * 40}%`,
              }}
              animate={{
                y: [-20, 20, -20],
                opacity: [0.2, 0.6, 0.2],
              }}
              transition={{
                duration: 2 + i * 0.5,
                repeat: Infinity,
                delay: i * 0.3,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

// Componente para lazy loading de páginas
interface LazyPageProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  delay?: number;
}

export const LazyPage: React.FC<LazyPageProps> = ({
  children,
  fallback = <PageLoader />,
  delay = 200
}) => {
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  if (isLoading) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};
